import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import type { AncLanguageOption } from '@libs/common-interceptor';
import { LocalStorageService } from './local-storage.service';
import { GetterSetterService } from './getter-setter.service';
import { User } from '../../models/users';

@Injectable({
    providedIn: 'root'
})
export class TranslationService {
    public user: User;
    public changeLang;
    constructor(
        private http: HttpClient,
        private translate: TranslateService,
        private localStorageService: LocalStorageService,
        private getterSetter: GetterSetterService
    ) {
        this.user = new User();
    }
    public monthsShort = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ];
    public months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];

    public getBrowsersLocals() {
        const user = this.localStorageService.get('userData');
        if (user && user?.current_translation) {
            this.changeLang = user.current_translation || 'en';
        } else {
            this.changeLang = 'en';
        }
        this.translate.addLangs([
            'en',
            'id',
            'ja',
            'ko',
            'ms',
            'tg',
            'th',
            'vi',
            'zh',
            'fr',
            'ar',
            'ch'
        ]);
        this.translate.setDefaultLang(this.changeLang);
        // let browserLang = this.translate.getBrowserLang();
        const language = this.changeLang.match(
            /fr|en|id|ja|ko|ms|tg|th|vi|zh|ar|ch/
        )
            ? this.changeLang
            : 'en';
        this.translate.use(language);
        this.monthsShort.forEach((item, index) => {
            this.monthsShort[index] = this.translate.instant(
                this.monthsShort[index]
            );
            this.months[index] = this.translate.instant(this.months[index]);
        });
        return language;
    }

    /**
     * Re-applies the user's saved locale and document direction (LTR/RTL).
     * Call from lazy-loaded module shells so i18n is not reset on navigation.
     */
    public initializeLanguageFromProfile(): string {
        const language = this.getBrowsersLocals();
        this.syncDocumentDirection(language);
        return language;
    }

    /** Sets `html[dir]` and {@link GetterSetterService.rtl} from the active locale. */
    public syncDocumentDirection(language: string): void {
        const isRtl = language === 'ar';
        this.getterSetter.rtl = isRtl;
        const html = document.documentElement;
        if (isRtl) {
            html.setAttribute('dir', 'rtl');
        } else if (html.hasAttribute('dir')) {
            html.removeAttribute('dir');
        }
    }

    /**
     * Persists the selected language and reloads the page so i18n and layout refresh.
     *
     * @param option Selected language from {@link AncLanguageSelectorComponent}.
     */
    public applyLanguageSelection(option: AncLanguageOption): void {
        const user = this.localStorageService.get('userData');
        user['current_translation'] = option.option_key;
        user['current_language'] = option.display;
        this.localStorageService.set('userData', user);
        const language = this.getBrowsersLocals();
        this.translate.use(language);
        window.location.reload();
    }

    /**
     * Returns ngbPopover / ngbDropdown placement based on document direction.
     */
    public getLanguageDropdownPlacement(): string {
        const htmlElement = document.getElementsByTagName('html')[0];
        const isRtl = htmlElement?.getAttribute('dir') === 'rtl';
        return isRtl ? 'bottom-start' : 'bottom-end';
    }

    /** Whether the language selector should render (hidden for DPD logistic tenants). */
    public isLanguageSelectorVisible(): boolean {
        return !this.localStorageService.get('isDpdLogistic');
    }

    /** Active locale key from user profile, e.g. `en`, `fr`. Defaults to English. */
    public getCurrentLanguageKey(): string {
        const user = this.localStorageService.get('userData');
        return user?.current_translation ?? 'en';
    }
}
